import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:      <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:     <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ShieldCheck:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Shield:         <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    CheckCircle:    <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    XCircle:        <><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></>,
    AlertTriangle:  <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    Clock:          <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Wallet:         <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    RefreshCw:      <><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></>,
    Star:           <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Users:          <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Home:           <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Wrench:         <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    BadgeCheck:     <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Zap:            <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Scale:          <><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></>,
    MessageCircle:  <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    ChevronDown:    <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:      <><path d="m18 15-6-6-6 6"/></>,
    Lock:           <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    Phone:          <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    FileText:       <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── What's covered / not covered accordion ── */
function CoverageItem({ icon, iconColor, iconBg, iconBorder, title, desc, covered }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 14,
        background: "none", border: "none", padding: "20px 0",
        cursor: "pointer", fontFamily: T.sans, textAlign: "left",
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: iconBg, border: `1px solid ${iconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={icon} size={16} color={iconColor} strokeWidth={1.8} />
        </div>
        <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: T.n800, lineHeight: 1.45 }}>{title}</span>
        <span style={{
          width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, fontWeight: 300, lineHeight: 1,
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 260 : 0, transition: "max-height .3s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: T.n600, lineHeight: 1.75, paddingLeft: 50 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ── Process step ── */
function ProcessStep({ n, title, desc, icon, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {/* Line connector */}
      {!isLast && (
        <div style={{ position: "absolute", left: 19, top: 44, bottom: -24, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})` }} />
      )}
      {/* Step circle */}
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={icon} size={17} color={T.g600} strokeWidth={1.7} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 40 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", fontFamily: T.sans }}>STEP {n}</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em", fontFamily: T.sans }}>{title}</div>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0, fontFamily: T.sans }}>{desc}</p>
      </div>
    </div>
  );
}

/* ── Scenario card ── */
function ScenarioCard({ icon, title, situation, outcome, tag, tagColor, tagBg }) {
  return (
    <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, padding: "24px", background: T.white, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={icon} size={18} color={T.g600} strokeWidth={1.6} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: tagColor, background: tagBg, border: `1px solid ${tagColor}22`, borderRadius: 99, padding: "3px 10px", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{tag}</span>
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6, fontFamily: T.sans, letterSpacing: "-0.01em" }}>{title}</div>
        <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: "0 0 12px", fontFamily: T.sans }}><strong style={{ color: T.n700 }}>What happened:</strong> {situation}</p>
        <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 12 }}>
          <p style={{ fontSize: 13, color: T.g700, lineHeight: 1.7, margin: 0, fontFamily: T.sans }}><strong>Outcome:</strong> {outcome}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Stat pill ── */
function StatPill({ value, label }) {
  return (
    <div style={{ padding: "18px 22px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50, flex: "1 1 120px" }}>
      <div style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,34px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 5 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.n400, lineHeight: 1.4 }}>{label}</div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function JiraniGuaranteePage() {
  useFonts();
  const navigate = useNavigate();

  const COVERED = [
    {
      icon: "Home", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Pro doesn't show up for a confirmed booking",
      desc: "If a JiraniPro confirms a booking and then does not arrive — with no prior communication and no valid reason — you are entitled to a full refund of any amount paid. We will also help you rebook with another available Pro at no extra cost. Last-minute cancellations by Pros are flagged on their profile and affect their standing on the platform.",
    },
    {
      icon: "Wrench", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Work is incomplete or materially different from what was agreed",
      desc: "If a Pro delivers work that is incomplete, or substantially different from the scope agreed at booking — for example, only cleaning one room when three were agreed, or painting the wrong surface — you can open a dispute within 48 hours of job completion. Our team reviews evidence from both sides and may issue a full or partial refund proportional to what was not delivered.",
    },
    {
      icon: "Home", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Property damaged by a JiraniPro during a job",
      desc: "All verified JiraniPros are covered under our damage protection policy. If a Pro accidentally damages property in your home while carrying out a booked service, file a dispute with supporting photos within 48 hours. We mediate the claim and, where fault is clear, arrange compensation or repair. This applies to incidental damage, not pre-existing conditions.",
    },
    {
      icon: "Zap", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Charged more than the agreed or quoted price",
      desc: "The price shown at booking confirmation is the price you pay for the agreed scope of work. If a Pro attempts to charge more than what was confirmed — without your prior agreement to a scope change — you are protected. We will review the transaction and refund any overcharge. If scope genuinely changed mid-job, both parties must agree to it before extra charges apply.",
    },
    {
      icon: "BadgeCheck", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Safety concerns or unprofessional conduct",
      desc: "If a JiraniPro behaves in a way that makes you feel unsafe, acts in a threatening manner, or violates your privacy while on your premises, report it immediately. Our Trust & Safety team investigates within 24 hours. Depending on findings, the Pro may be suspended or permanently removed from the platform. Your safety is not a customer service issue — it is our fundamental obligation.",
    },
    {
      icon: "Lock", iconColor: T.g600, iconBg: T.g50, iconBorder: T.g100,
      title: "Fraudulent activity or identity misrepresentation",
      desc: "If a Pro's profile does not match the person who arrived, or if you suspect the Pro's verification documents were fraudulent, report it immediately. We take identity fraud on the platform extremely seriously. Affected bookings are fully refunded and the account is suspended pending investigation.",
    },
  ];

  const NOT_COVERED = [
    {
      icon: "AlertTriangle", iconColor: T.amber, iconBg: T.amberBg, iconBorder: T.amberBorder,
      title: "Disputes raised more than 48 hours after job completion",
      desc: "The Jirani Guarantee applies to disputes submitted within 48 hours of a job being marked complete. After this window closes, we are unable to verify circumstances reliably enough to adjudicate fairly. If something goes wrong, act quickly — open a dispute the same day if possible.",
    },
    {
      icon: "AlertTriangle", iconColor: T.amber, iconBg: T.amberBg, iconBorder: T.amberBorder,
      title: "Work arranged or paid outside the JiraniApp platform",
      desc: "The Guarantee only covers bookings made and paid through JiraniApp. If you agreed a job directly with a Pro via phone, WhatsApp, or cash, outside the app, that transaction is not covered. This is one of the core reasons we ask you to book and pay through the platform — it protects you.",
    },
    {
      icon: "AlertTriangle", iconColor: T.amber, iconBg: T.amberBg, iconBorder: T.amberBorder,
      title: "Dissatisfaction based on personal preference, not quality",
      desc: "The Guarantee covers objective failures — a no-show, incomplete work, agreed scope not delivered. It does not apply to subjective dissatisfaction where the work was completed as agreed but you later decided you prefer a different result. If you have a preference about how a job should be done, communicate it clearly at booking.",
    },
    {
      icon: "AlertTriangle", iconColor: T.amber, iconBg: T.amberBg, iconBorder: T.amberBorder,
      title: "Damage caused by pre-existing conditions not disclosed",
      desc: "If property damage occurs because of a pre-existing fault that the Pro could not reasonably have identified — for example, old pipes that burst during a standard plumbing job — this falls outside the Guarantee's damage protection. We ask clients to disclose known issues with their property at booking to help Pros assess risk.",
    },
    {
      icon: "AlertTriangle", iconColor: T.amber, iconBg: T.amberBg, iconBorder: T.amberBorder,
      title: "Cancellations made by the client within 24 hours",
      desc: "Late cancellations made by the client within 24 hours of a scheduled job may incur a cancellation fee to compensate the Pro for reserved time. This is not a situation where the Guarantee applies — the protection runs in the direction of the client when a Pro fails to perform, not in reverse.",
    },
  ];

  const SCENARIOS = [
    {
      icon: "Home", title: "Cleaner no-shows on moving day",
      situation: "A client in Kilimani booked an end-of-tenancy clean for 8 am, the morning of their move-out. The Pro confirmed the night before but did not arrive. Phone calls went unanswered.",
      outcome: "Full refund processed within 2 hours of the dispute being raised. Our team found an available Pro who arrived by 10 am. The client made their noon handover. The Pro's account was flagged with a no-show record.",
      tag: "Full refund", tagColor: T.g600, tagBg: T.g50,
    },
    {
      icon: "Wrench", title: "Electrician quotes one price, invoices another",
      situation: "A client in Westlands was quoted KES 3,500 for socket installation. After completion, the Pro asked for KES 6,200, claiming additional materials were used without prior discussion.",
      outcome: "Dispute reviewed against the original booking confirmation. The Pro could not show evidence the client agreed to the scope change. The KES 2,700 difference was refunded within 3 business days. The Pro received a formal pricing conduct warning.",
      tag: "Partial refund", tagColor: T.amber, tagBg: T.amberBg,
    },
    {
      icon: "Home", title: "Painter leaves job half-finished",
      situation: "A client in Karen booked interior painting for a 3-bedroom home over 2 days. The Pro completed one room and did not return on day 2. Multiple contacts went unanswered.",
      outcome: "Dispute upheld. Two-thirds of the booking fee was refunded to reflect the unfinished work. The Pro's account was suspended pending a conduct review. The client was fast-tracked to a replacement Pro who completed the job.",
      tag: "Partial refund", tagColor: T.amber, tagBg: T.amberBg,
    },
    {
      icon: "BadgeCheck", title: "Pro arrives but does not match their profile",
      situation: "A client in Nyali booked a specific verified plumber. A different person arrived claiming to be 'an assistant' without any prior notice or agreement from the client.",
      outcome: "Client raised a safety concern. Job was cancelled immediately. Full refund issued. The original Pro's account was suspended pending an identity and delegation policy review. Clients are never obligated to allow someone different from the booked Pro into their home.",
      tag: "Full refund + safety review", tagColor: T.g600, tagBg: T.g50,
    },
  ];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="ShieldCheck" size={14} color={T.g500} strokeWidth={2.2} />
            Client protection
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,68px)", fontWeight: 400, margin: "0 0 20px", lineHeight: 1.06, color: T.n900, letterSpacing: "-0.03em", maxWidth: 620 }}>
            The Jirani<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>Guarantee</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 0", maxWidth: 500 }}>
            Every booking on JiraniApp is backed by a clear, enforceable protection policy. 
            Not fine print. Not vague promises. Specific commitments about what we will do when things go wrong.
          </p>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "Clock",      label: "Disputes reviewed within 24 hours" },
            { icon: "Wallet",     label: "Refunds processed in 3–5 business days" },
            { icon: "ShieldCheck",label: "Applies to all verified JiraniPro bookings" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "24px 20px", display: "flex", alignItems: "center", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={18} color={T.g500} strokeWidth={1.5} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ WHAT IT IS ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What it is</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 20px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Protection that works, not just policy that <em style={{ fontStyle: "italic", color: T.g600 }}>sounds good</em>
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 32 }}>
            <div style={{ padding: "24px", borderRadius: 16, border: `1.5px solid ${T.g200}`, background: T.g50 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.g700, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="CheckCircle" size={16} color={T.g500} strokeWidth={2} />
                What it is
              </div>
              <p style={{ fontSize: 14, color: T.n700, lineHeight: 1.75, margin: 0 }}>
                A concrete commitment from JiraniApp to review and act on disputes, process refunds 
                where appropriate, and protect your interests when a booking doesn't go as agreed.
              </p>
            </div>
            <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.n50 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.n700, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="XCircle" size={16} color={T.n400} strokeWidth={2} />
                What it isn't
              </div>
              <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>
                It isn't a blanket refund policy for any dissatisfaction, or an insurance product. 
                It covers specific, demonstrable failures — a no-show, incomplete work, billing discrepancies, 
                and safety concerns.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <StatPill value="24h" label="Dispute review window" />
            <StatPill value="3–5" label="Days to refund (M-Pesa: 24h)" />
            <StatPill value="48h" label="Window to raise a dispute" />
            <StatPill value="100%" label="Verified booking coverage" />
          </div>
        </div>

        {/* ══ WHAT'S COVERED ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>Full protection</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What the Guarantee <em style={{ fontStyle: "italic", color: T.g600 }}>covers</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 4px", lineHeight: 1.65 }}>
            These situations are covered for all verified bookings. Tap any item for the full detail.
          </p>
          <div style={{ marginTop: 8 }}>
            {COVERED.map((item, i) => (
              <CoverageItem key={i} {...item} covered={true} />
            ))}
          </div>
        </div>

        {/* ══ WHAT'S NOT COVERED ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.amber, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>Important limits</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What it doesn't <em style={{ fontStyle: "italic", color: T.n600 }}>cover</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 4px", lineHeight: 1.65 }}>
            Being clear about the limits is part of the promise. No surprises.
          </p>
          <div style={{ marginTop: 8 }}>
            {NOT_COVERED.map((item, i) => (
              <CoverageItem key={i} {...item} covered={false} />
            ))}
          </div>
        </div>

        {/* ══ HOW TO CLAIM ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>How to claim</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Raising a dispute is <em style={{ fontStyle: "italic", color: T.g600 }}>straightforward</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            No call centres, no lengthy forms. Here is exactly what happens when you open a dispute.
          </p>

          <div style={{ paddingLeft: 4 }}>
            <ProcessStep n={1} icon="FileText" isLast={false}
              title="Open a dispute from your Bookings page"
              desc="Go to Bookings, select the relevant job, and tap 'Report an Issue' or 'Start Dispute'. This is available for 48 hours after the job is marked complete. If the Pro never showed up, you can open a dispute from the moment the scheduled start time passes."
            />
            <ProcessStep n={2} icon="FileText" isLast={false}
              title="Describe what happened and add evidence"
              desc="Tell us what was agreed and what actually occurred. Include the booking reference, the issue type, and any supporting evidence — photos, screenshots of conversations with the Pro, receipts, or any other documentation that helps establish what went wrong. The more specific you are, the faster we can act."
            />
            <ProcessStep n={3} icon="Users" isLast={false}
              title="We notify the Pro and gather their response"
              desc="Once your dispute is received, we notify the Pro and give them a chance to provide their account of events. We review both sides. For straightforward cases — a confirmed no-show, for example — this step may be brief. For complex cases involving disputed scope or quality, we may request additional information from both parties."
            />
            <ProcessStep n={4} icon="Scale" isLast={false}
              title="Our team reviews and makes a determination"
              desc="Our Trust & Safety team reviews all submitted evidence and makes a fair determination within 24–48 hours. We will notify you of the outcome via SMS and email. If a refund is due, we confirm the amount and method at this stage. If the dispute is not upheld, we explain why in writing."
            />
            <ProcessStep n={5} icon="Wallet" isLast={true}
              title="Refund is processed if the dispute is upheld"
              desc="Where a refund is approved, it is processed within 3–5 business days. M-Pesa refunds typically arrive within 24 hours of processing. Card refunds take 3–5 days depending on your bank. You will receive a confirmation when the refund is dispatched. If you disagree with the outcome, you can appeal once by requesting a senior review."
            />
          </div>

          {/* CTA inline */}
          <div style={{ marginTop: 36, padding: "22px 24px", borderRadius: 14, border: `1px solid ${T.g100}`, background: T.g50, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 4 }}>Need to raise a dispute right now?</div>
              <div style={{ fontSize: 13, color: T.n600 }}>Go to Help & Support to start the process.</div>
            </div>
            <button onClick={() => navigate("/help")} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "12px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
              whiteSpace: "nowrap", flexShrink: 0,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start a dispute
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* ══ REAL SCENARIOS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>Real examples</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            How the Guarantee plays out <em style={{ fontStyle: "italic", color: T.g600 }}>in practice</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            The following are illustrative examples based on real dispute types we see on the platform. Names and specific details are representative, not verbatim cases.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {SCENARIOS.map((s, i) => <ScenarioCard key={i} {...s} />)}
          </div>
        </div>

        {/* ══ PRO CONDUCT ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(28px,4vw,40px)", borderRadius: 18, border: `1px solid ${T.n150}`, background: T.n50 }}>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 24 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="BadgeCheck" size={20} color={T.n600} strokeWidth={1.6} />
            </div>
            <div>
              <h3 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3.5vw,26px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                The other side: what we require from JiraniPros
              </h3>
              <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.7, margin: 0 }}>
                The Guarantee is only possible because we set clear standards for every Pro on the platform. These are not aspirational guidelines — they are conditions of remaining listed on JiraniApp.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[
              { title: "Arrive for confirmed bookings", desc: "A confirmed booking is a professional commitment. Repeated no-shows result in permanent removal from the platform, regardless of stated reasons." },
              { title: "Deliver the agreed scope of work", desc: "Pros are required to complete what was confirmed at booking or communicate changes to scope — and get client agreement — before carrying them out." },
              { title: "Charge only the agreed or updated price", desc: "No post-completion price increases without prior client consent. Billing disputes are taken seriously and affect a Pro's standing." },
              { title: "Respect the client's property and privacy", desc: "Pros must conduct themselves with care in every home they enter. Damage, privacy breaches, and misconduct are grounds for immediate suspension." },
              { title: "Respond to disputes within the review window", desc: "When a dispute is raised, Pros are expected to participate in the review process honestly. Non-response is treated as non-cooperation and weighted accordingly." },
            ].map((item, i, arr) => (
              <div key={i} style={{ display: "flex", gap: 14, padding: "18px 0", borderBottom: i < arr.length - 1 ? `1px solid ${T.n150}` : "none" }}>
                <div style={{ marginTop: 3, flexShrink: 0 }}>
                  <Icon name="CheckCircle" size={16} color={T.g500} strokeWidth={2} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 4 }}>{item.title}</div>
                  <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ IMPORTANT NOTES ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>Good to know</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 20px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            A few things worth <em style={{ fontStyle: "italic", color: T.g600 }}>knowing</em>
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { icon: "MessageCircle", title: "Evidence helps, but it isn't always required", desc: "For clear-cut failures like a no-show — where the booking is confirmed and the Pro simply does not arrive — we do not require photos or documentation. For disputes about quality or scope, supporting evidence (photos, screenshots of agreed terms) significantly speeds up the review and improves the outcome." },
              { icon: "RefreshCw",     title: "You can appeal once", desc: "If a dispute determination is not in your favour and you believe the decision was made on incomplete information, you have one right of appeal. Contact us with any new evidence or information and request a senior review. This is handled by a different member of the team than the original case." },
              { icon: "Lock",          title: "Book and pay in-app to stay protected", desc: "The Jirani Guarantee only applies to bookings made and paid through JiraniApp. If you pay cash directly to a Pro, or arrange a job outside the platform after finding them through JiraniApp, that transaction falls outside our protection. Always complete payment through the app." },
              { icon: "Phone",         title: "You can call us for urgent safety concerns", desc: "For anything involving a safety concern — threatening behaviour, refusal to leave your property, or any situation where you feel at immediate risk — call our Trust & Safety line directly rather than raising a standard dispute. The number is in the app under Help & Support." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 16, padding: "20px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.white }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={item.icon} size={16} color={T.n500} strokeWidth={1.7} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 5 }}>{item.title}</div>
                  <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(32px,5vw,52px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="ShieldCheck" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Book with confidence
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.7, maxWidth: 420 }}>
            Every JiraniPro booking is backed by this Guarantee. Find a professional near you and book knowing you're protected.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/search")}
              style={{ padding: "13px 28px", background: T.g500, color: T.white, border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em", display: "inline-flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a JiraniPro
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/help")}
              style={{ padding: "13px 28px", background: "transparent", color: T.n700, border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Help & Support
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}